/**
 * Getting one whole product folder out of the browser, with its shape intact.
 *
 * Two routes reach the same place, because the two gestures an administrator
 * naturally makes are different APIs:
 *
 *  - "Choose Product Folder" uses <input type="file" webkitdirectory>, which
 *    reports each file's path inside the chosen folder as webkitRelativePath.
 *  - Dragging a folder onto the page gives DataTransferItems, which must be
 *    walked through the (webkit)getAsEntry filesystem API to recover the same
 *    structure. A plain drop reports only the top-level entry, so without the
 *    walk a dropped folder arrives as nothing at all.
 *
 * Both are browser-native. Nothing here needs a drag-and-drop package: the only
 * thing a package would add is styling around the same two APIs.
 *
 * The relative path is what makes the folder readable as a folder — colour is a
 * directory level, not a filename convention — so it is preserved exactly and
 * never flattened to a bare file list.
 */

import type { PickedFile } from "./glassesFolderParser";

/** The subset of the non-standard filesystem entry API we rely on. */
interface FsEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
}

interface FsFileEntry extends FsEntry {
  file(onSuccess: (file: File) => void, onError: (err: unknown) => void): void;
}

interface FsDirectoryEntry extends FsEntry {
  createReader(): {
    readEntries(
      onSuccess: (entries: FsEntry[]) => void,
      onError: (err: unknown) => void,
    ): void;
  };
}

function toPicked(file: File, relativePath: string): PickedFile {
  return { relativePath, name: file.name, size: file.size, file };
}

/**
 * Files from a directory <input>.
 *
 * webkitRelativePath already carries "<root>/<colour>/<file>", which is exactly
 * the shape the parser reads.
 */
export function filesFromDirectoryInput(list: FileList | null): PickedFile[] {
  if (!list) return [];
  return Array.from(list).map((file) => {
    const relative = (file as File & { webkitRelativePath?: string })
      .webkitRelativePath;
    return toPicked(file, relative && relative.length > 0 ? relative : file.name);
  });
}

function readAllEntries(directory: FsDirectoryEntry): Promise<FsEntry[]> {
  // readEntries yields at most 100 entries per call and signals the end with an
  // empty batch, so it has to be drained in a loop. Reading it once silently
  // truncates a large folder.
  const reader = directory.createReader();
  const all: FsEntry[] = [];

  return new Promise((resolve, reject) => {
    const readBatch = () => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(all);
          return;
        }
        all.push(...batch);
        readBatch();
      }, reject);
    };
    readBatch();
  });
}

async function walkEntry(entry: FsEntry, prefix: string): Promise<PickedFile[]> {
  const path = prefix ? `${prefix}/${entry.name}` : entry.name;

  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) =>
      (entry as FsFileEntry).file(resolve, reject),
    );
    return [toPicked(file, path)];
  }

  if (entry.isDirectory) {
    const children = await readAllEntries(entry as FsDirectoryEntry);
    const nested = await Promise.all(children.map((child) => walkEntry(child, path)));
    return nested.flat();
  }

  return [];
}

/**
 * Files from a drop, with the dropped folder's structure recovered.
 *
 * Falls back to the plain file list when the browser offers no entry API, so a
 * drop still produces something the parser can judge — it will simply report a
 * flat selection as not being a product folder, which is the honest answer.
 */
export async function filesFromDrop(transfer: DataTransfer): Promise<PickedFile[]> {
  const items = Array.from(transfer.items ?? []);

  const entries = items
    .map((item) => {
      const withEntry = item as DataTransferItem & {
        webkitGetAsEntry?: () => FsEntry | null;
        getAsEntry?: () => FsEntry | null;
      };
      const get = withEntry.webkitGetAsEntry ?? withEntry.getAsEntry;
      return typeof get === "function" ? get.call(withEntry) : null;
    })
    .filter((entry): entry is FsEntry => entry !== null);

  if (entries.length === 0) {
    return Array.from(transfer.files ?? []).map((file) => toPicked(file, file.name));
  }

  const walked = await Promise.all(entries.map((entry) => walkEntry(entry, "")));
  return walked.flat();
}
